import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/apiClient';
import { isElectron } from '../../utils/electronBridge';

/**
 * UsageBanner - Dismissible banner for credit/sync usage warnings
 * Appears at the top of the editor area. Shows the most critical warning.
 * Priority: no AI credits > no voice credits > low AI > low voice > sync limited > local-only
 */

const BANNER_STATES = {
  NO_AI_CREDITS: 'no_ai_credits',
  NO_VOICE_CREDITS: 'no_voice_credits',
  LOW_AI_CREDITS: 'low_ai_credits',
  LOW_VOICE_CREDITS: 'low_voice_credits',
  SYNC_LIMITED: 'sync_limited',
  LOCAL_ONLY: 'local_only',
};

const LOW_THRESHOLD = 0.2; // 20% of allocated

// Sustained-offline only (per openspec/changes/web-server-source-of-truth
// task 5.2): a brief network blip (a few seconds to ~couple minutes) with
// the tab still open is absorbed transparently by the debounced push's own
// retry-on-reconnect -- it should NOT flash this banner. Only offline for
// longer than this threshold surfaces it.
const OFFLINE_SUSTAIN_MS = 30000;

const getDismissKey = (state) => `usageBanner_dismissed_${state}`;

const UsageBanner = ({ isDarkMode }) => {
  const navigate = useNavigate();
  const [creditData, setCreditData] = useState(null);
  const [syncLimited, setSyncLimited] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const offlineTimerRef = useRef(null);
  const [dismissed, setDismissed] = useState({});
  const [visible, setVisible] = useState(false);

  // Fetch credit data on mount
  const fetchCredits = useCallback(async () => {
    try {
      const data = await api.get('/api/usage/credits');

      // Handle old format fallback: { tokens: {...} } -> normalize to new format
      if (data && data.tokens && !data.aiCredits) {
        const tokens = data.tokens;
        setCreditData({
          aiCredits: {
            allocated: tokens.allocated || 0,
            used: tokens.used || 0,
            remaining: tokens.remaining || 0,
          },
          voiceCredits: {
            allocated: 0,
            used: 0,
            remaining: 0,
          },
        });
      } else if (data) {
        setCreditData({
          aiCredits: data.aiCredits || { allocated: 0, used: 0, remaining: 0 },
          voiceCredits: data.voiceCredits || { allocated: 0, used: 0, remaining: 0 },
        });
      }
    } catch (err) {
      // Silently fail - banner just won't show credit warnings
      console.warn('[UsageBanner] Failed to fetch credits:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Listen for custom events
  useEffect(() => {
    const handleCreditsUpdated = () => fetchCredits();
    const handleSyncLimited = () => setSyncLimited(true);

    const armOfflineTimer = () => {
      if (offlineTimerRef.current) return;
      offlineTimerRef.current = setTimeout(() => {
        offlineTimerRef.current = null;
        setIsOffline(true);
      }, OFFLINE_SUSTAIN_MS);
    };
    const handleOnline = () => {
      if (offlineTimerRef.current) {
        clearTimeout(offlineTimerRef.current);
        offlineTimerRef.current = null;
      }
      setIsOffline(false);
    };
    const handleOffline = () => armOfflineTimer();

    window.addEventListener('creditsUpdated', handleCreditsUpdated);
    window.addEventListener('syncLimited', handleSyncLimited);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Already offline at mount (e.g. page loaded while disconnected) --
    // still apply the same sustain threshold, not an instant banner.
    if (!navigator.onLine) armOfflineTimer();

    return () => {
      window.removeEventListener('creditsUpdated', handleCreditsUpdated);
      window.removeEventListener('syncLimited', handleSyncLimited);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (offlineTimerRef.current) clearTimeout(offlineTimerRef.current);
    };
  }, [fetchCredits]);

  // Load dismissed states from sessionStorage
  useEffect(() => {
    const stored = {};
    Object.values(BANNER_STATES).forEach((state) => {
      const key = getDismissKey(state);
      if (sessionStorage.getItem(key) === 'true') {
        stored[state] = true;
      }
    });
    setDismissed(stored);
  }, []);

  // Determine which banner state to show (priority order)
  const activeBannerState = useMemo(() => {
    const ai = creditData?.aiCredits;
    const voice = creditData?.voiceCredits;

    const states = [];

    // Priority 1: No AI credits
    if (ai && ai.allocated > 0 && ai.remaining === 0) {
      states.push(BANNER_STATES.NO_AI_CREDITS);
    }

    // Priority 2: No voice credits
    if (voice && voice.allocated > 0 && voice.remaining === 0) {
      states.push(BANNER_STATES.NO_VOICE_CREDITS);
    }

    // Priority 3: Low AI credits
    if (ai && ai.allocated > 0 && ai.remaining > 0 && ai.remaining < ai.allocated * LOW_THRESHOLD) {
      states.push(BANNER_STATES.LOW_AI_CREDITS);
    }

    // Priority 4: Low voice credits
    if (voice && voice.allocated > 0 && voice.remaining > 0 && voice.remaining < voice.allocated * LOW_THRESHOLD) {
      states.push(BANNER_STATES.LOW_VOICE_CREDITS);
    }

    // Priority 5: Sync limited
    if (syncLimited) {
      states.push(BANNER_STATES.SYNC_LIMITED);
    }

    // Priority 6: Local-only / offline
    if (isOffline) {
      states.push(BANNER_STATES.LOCAL_ONLY);
    }

    // Return first non-dismissed state
    for (const state of states) {
      if (!dismissed[state]) {
        return state;
      }
    }

    return null;
  }, [creditData, syncLimited, isOffline, dismissed]);

  // Animate in when there's an active state
  useEffect(() => {
    if (activeBannerState) {
      // Small delay so the slide-down animation is visible
      const timer = setTimeout(() => setVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [activeBannerState]);

  const handleDismiss = useCallback((state) => {
    sessionStorage.setItem(getDismissKey(state), 'true');
    setDismissed((prev) => ({ ...prev, [state]: true }));
  }, []);

  const handleAction = useCallback((state) => {
    if (state === BANNER_STATES.LOCAL_ONLY) {
      handleDismiss(state);
      return;
    }
    navigate('/settings#subscription');
  }, [navigate, handleDismiss]);

  // Banner config for each state
  const getBannerConfig = useCallback((state) => {
    const ai = creditData?.aiCredits;
    const voice = creditData?.voiceCredits;

    switch (state) {
      case BANNER_STATES.NO_AI_CREDITS:
        return {
          icon: 'fas fa-exclamation-circle',
          message: "You've run out of AI credits.",
          actionLabel: 'Buy Credits',
          type: 'red',
        };
      case BANNER_STATES.NO_VOICE_CREDITS:
        return {
          icon: 'fas fa-exclamation-circle',
          message: "You've run out of voice credits.",
          actionLabel: 'Buy Credits',
          type: 'red',
        };
      case BANNER_STATES.LOW_AI_CREDITS:
        return {
          icon: 'fas fa-exclamation-triangle',
          message: `You're running low on AI credits (${ai?.remaining ?? 0} remaining).`,
          actionLabel: 'Buy Credits',
          type: 'yellow',
        };
      case BANNER_STATES.LOW_VOICE_CREDITS:
        return {
          icon: 'fas fa-exclamation-triangle',
          message: `Running low on voice credits (${voice?.remaining ?? 0} min remaining).`,
          actionLabel: 'Buy Credits',
          type: 'yellow',
        };
      case BANNER_STATES.SYNC_LIMITED:
        return {
          icon: 'fas fa-cloud',
          message: 'Cloud sync is limited on the free plan.',
          actionLabel: 'Upgrade',
          type: 'blue',
        };
      case BANNER_STATES.LOCAL_ONLY:
        // Web has no offline editing story of its own (server is the
        // source of truth there) -- point to the desktop app instead of
        // Electron's own "saved to your device" message, which describes
        // real local-filesystem-mirror durability that only exists there.
        return isElectron ? {
          icon: 'fas fa-hdd',
          message: 'Working in local-only mode. Files are saved to your device.',
          actionLabel: 'Dismiss',
          type: 'gray',
        } : {
          icon: 'fas fa-wifi',
          message: "You're offline. The browser doesn't support offline editing -- download the desktop app to keep working without a connection.",
          actionLabel: 'Dismiss',
          type: 'gray',
        };
      default:
        return null;
    }
  }, [creditData]);

  if (!activeBannerState) return null;

  const config = getBannerConfig(activeBannerState);
  if (!config) return null;

  // Color schemes: [bg-light, text-light, bg-dark, text-dark, actionBg-light, actionBg-dark]
  const colorSchemes = {
    red: {
      bg: isDarkMode ? '#450a0a' : '#fef2f2',
      text: isDarkMode ? '#fca5a5' : '#7f1d1d',
      actionBg: isDarkMode ? '#7f1d1d' : '#dc2626',
      actionText: '#fff',
      iconColor: isDarkMode ? '#f87171' : '#dc2626',
    },
    yellow: {
      bg: isDarkMode ? '#451a03' : '#fffbeb',
      text: isDarkMode ? '#fcd34d' : '#78350f',
      actionBg: isDarkMode ? '#78350f' : '#d97706',
      actionText: '#fff',
      iconColor: isDarkMode ? '#fbbf24' : '#d97706',
    },
    blue: {
      bg: isDarkMode ? '#172554' : '#eff6ff',
      text: isDarkMode ? '#93c5fd' : '#1e3a5f',
      actionBg: isDarkMode ? '#1e40af' : '#2563eb',
      actionText: '#fff',
      iconColor: isDarkMode ? '#60a5fa' : '#2563eb',
    },
    gray: {
      bg: isDarkMode ? '#1f2937' : '#f9fafb',
      text: isDarkMode ? '#d1d5db' : '#1f2937',
      actionBg: isDarkMode ? '#374151' : '#6b7280',
      actionText: '#fff',
      iconColor: isDarkMode ? '#9ca3af' : '#6b7280',
    },
  };

  const colors = colorSchemes[config.type];

  return (
    <div
      style={{
        width: '100%',
        height: visible ? '40px' : '0px',
        overflow: 'hidden',
        transition: 'height 0.3s ease',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          height: '40px',
          padding: '0 12px',
          backgroundColor: colors.bg,
          color: colors.text,
          fontFamily: "'Inter', sans-serif",
          fontSize: '13px',
          boxSizing: 'border-box',
          borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          transform: visible ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'transform 0.3s ease',
        }}
      >
        {/* Left: icon + message */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            minWidth: 0,
            flex: 1,
          }}
        >
          <i
            className={config.icon}
            style={{
              fontSize: '13px',
              color: colors.iconColor,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: '12px',
              lineHeight: 1.3,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {config.message}
          </span>
        </div>

        {/* Right: action button + dismiss */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0,
            marginLeft: '12px',
          }}
        >
          <button
            onClick={() => handleAction(activeBannerState)}
            style={{
              padding: '4px 10px',
              fontSize: '11px',
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
              color: colors.actionText,
              backgroundColor: colors.actionBg,
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              lineHeight: 1.4,
              transition: 'opacity 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            {config.actionLabel}
          </button>
          <button
            onClick={() => handleDismiss(activeBannerState)}
            aria-label="Dismiss banner"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              padding: 0,
              fontSize: '12px',
              color: colors.text,
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer',
              opacity: 0.6,
              transition: 'opacity 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
          >
            <i className="fas fa-times" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default UsageBanner;
